"use client";

import { type ReactNode, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  divide,
  isExact,
  isZero,
  multiply,
  parseQuantity,
  plainText,
  type Ratio,
  scientificParts,
  toSignificant,
  type Written,
} from "@/lib/tool-kit/calendars/exact";

/** What a unit is: its id, its symbol (data, not prose) and its exact size in the base unit. */
export interface ExactUnit {
  id: string;
  symbol: string;
  factor: Ratio;
}

/** The words an exact converter shows. Each tool supplies its own English in its widget file. */
export interface ExactConverterStrings {
  valueLabel: string;
  valueHint: string;
  fromLabel: string;
  toLabel: string;
  digitsLabel: string;
  swap: string;
  resultHeading: string;
  plainLabel: string;
  scientificLabel: string;
  factorLabel: string;
  factorLine: string;
  exact: string;
  rounded: string;
  tableHeading: string;
  unitColumn: string;
  valueColumn: string;
  enterPrompt: string;
  invalidNumber: string;
  copy: string;
  copied: string;
  copySummary: string;
  unitNames: Readonly<Record<string, string>>;
  /** Heading for the "closest fit" card; needed only with `suggest`. */
  bestLabel?: string;
}

const DIGIT_CHOICES = [4, 6, 8, 10, 12, 15];
const SUPERSCRIPTS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const DIGIT = /\d/g;

function superscript(exponent: number): string {
  const sign = exponent < 0 ? "⁻" : "";
  return (
    sign +
    String(Math.abs(exponent)).replace(
      DIGIT,
      (digit) => SUPERSCRIPTS[Number(digit)] ?? digit
    )
  );
}

/** `1.602 × 10⁻¹⁹`. */
function scientificText(written: Written, mark: string): string {
  const { mantissa, exponent } = scientificParts(written, mark);
  return written.digits === "0"
    ? "0"
    : `${mantissa} × 10${superscript(exponent)}`;
}

/** Plain decimals for everyday sizes, scientific notation for the extremes. */
function compactText(written: Written, mark: string): string {
  const plain =
    written.exponent >= -6 && written.exponent <= 14
      ? plainText(written, mark)
      : null;
  return plain ?? scientificText(written, mark);
}

function convertAmount(target: ExactUnit, source: ExactUnit, quantity: Ratio) {
  return divide(multiply(quantity, source.factor), target.factor);
}

function Result({
  amount,
  from,
  to,
  units,
  typed,
  significant,
  mark,
  strings,
  suggest,
}: {
  amount: Ratio;
  from: ExactUnit;
  to: ExactUnit;
  units: readonly ExactUnit[];
  typed: string;
  significant: number;
  mark: string;
  strings: ExactConverterStrings;
  suggest?: (exponent: number) => string;
}) {
  const fmt = useToolNumber();
  const result = convertAmount(to, from, amount);
  const written = toSignificant(result, significant);
  const resultText = compactText(written, mark);
  const base = multiply(amount, from.factor);
  const bestId =
    suggest && strings.bestLabel && !isZero(base)
      ? suggest(toSignificant(base, 1).exponent)
      : undefined;
  const bestUnit = units.find((unit) => unit.id === bestId);
  return (
    <>
      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>
          {typed} {from.symbol} = {resultText} {to.symbol}
        </h2>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.plainLabel}
            value={
              plainText(written, mark, 30) ?? scientificText(written, mark)
            }
          />
          <StatCard
            label={strings.scientificLabel}
            value={scientificText(written, mark)}
          />
          {bestUnit ? (
            <StatCard
              label={strings.bestLabel ?? ""}
              value={`${compactText(toSignificant(convertAmount(bestUnit, from, amount), significant), mark)} ${bestUnit.symbol}`}
            />
          ) : null}
          {isZero(amount) ? null : (
            <StatCard
              label={strings.factorLabel}
              value={t(strings.factorLine, {
                from: from.symbol,
                factor: compactText(
                  toSignificant(divide(from.factor, to.factor), significant),
                  mark
                ),
                to: to.symbol,
              })}
            />
          )}
        </StatGrid>
        <p className="text-muted-foreground text-sm">
          {isExact(result, written)
            ? strings.exact
            : t(strings.rounded, { digits: fmt(significant) })}
        </p>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={t(strings.copySummary, {
            value: typed,
            from: from.symbol,
            result: resultText,
            to: to.symbol,
          })}
        />
      </section>
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
        <DataTable
          head={[strings.unitColumn, strings.valueColumn]}
          rows={units.map((unit) => [
            strings.unitNames[unit.id] ?? unit.id,
            compactText(
              toSignificant(convertAmount(unit, from, amount), significant),
              mark
            ),
          ])}
        />
      </section>
    </>
  );
}

export default function ExactConverter({
  idPrefix,
  strings,
  units,
  defaults,
  notes = [],
  suggest,
}: {
  idPrefix: string;
  strings: ExactConverterStrings;
  units: readonly ExactUnit[];
  defaults: { value: string; from: string; to: string };
  notes?: readonly string[];
  /** Picks the unit that reads best for a value whose first digit has this power of ten. */
  suggest?: (exponent: number) => string;
}) {
  const fmt = useToolNumber();
  const mark = fmt(1.5, {
    minimumFractionDigits: 1,
    useGrouping: false,
  }).charAt(1);
  const [value, setValue] = useState(defaults.value);
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [digits, setDigits] = useState("10");

  const significant = Number(digits);
  const fromUnit = units.find((unit) => unit.id === from) ?? units[0];
  const toUnit = units.find((unit) => unit.id === to) ?? units[0];
  const amount = value.trim() === "" ? null : parseQuantity(value);
  const options = units.map((unit) => ({
    value: unit.id,
    label: strings.unitNames[unit.id] ?? unit.id,
  }));

  let body: ReactNode = null;
  if (value.trim() === "") {
    body = <Notice>{strings.enterPrompt}</Notice>;
  } else if (amount === null || !fromUnit || !toUnit) {
    body = <ErrorNotice>{strings.invalidNumber}</ErrorNotice>;
  } else {
    body = (
      <Result
        amount={amount}
        from={fromUnit}
        mark={mark}
        significant={significant}
        strings={strings}
        suggest={suggest}
        to={toUnit}
        typed={value.trim()}
        units={units}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-[2fr_3fr_3fr]">
        <TextField
          hint={strings.valueHint}
          id={`${idPrefix}-value`}
          label={strings.valueLabel}
          onChange={setValue}
          value={value}
        />
        <SelectField
          id={`${idPrefix}-from`}
          label={strings.fromLabel}
          onChange={setFrom}
          options={options}
          value={from}
        />
        <SelectField
          id={`${idPrefix}-to`}
          label={strings.toLabel}
          onChange={setTo}
          options={options}
          value={to}
        />
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setFrom(to);
            setTo(from);
          }}
          type="button"
        >
          {strings.swap}
        </button>
        <div className="w-40">
          <SelectField
            id={`${idPrefix}-digits`}
            label={strings.digitsLabel}
            onChange={setDigits}
            options={DIGIT_CHOICES.map((choice) => ({
              value: String(choice),
              label: fmt(choice),
            }))}
            value={digits}
          />
        </div>
      </div>
      {body}
      {notes.map((note) => (
        <Notice key={note}>{note}</Notice>
      ))}
    </div>
  );
}
